// ModeToggle — top-right 3-segment switch that flips the planet
// between NARRATIVE / EXPLORE / TOUR modes. The layout is a frosted
// pill with three equal segments; the active segment lights up with a
// mint underline + brighter label. The mode is read from
// window.__planetMode (via useModeController's bus).
//
// Icons (8-9px SVGs, currentColor):
//   - narrative:  compass (circle + rotated lozenge)
//   - exploration: orbit (ellipse with center dot)
//   - tour:       play (rounded triangle, forward-leaning)
//
// The pill cycles through the three modes in order; clicking anywhere
// on a segment jumps to that mode directly (more discoverable than
// cycling). The T keyboard shortcut mirrors toggle().

import { useEffect, useState } from 'react';
import { track } from '../utils/analytics';
import './mode-toggle.css';

type Mode = 'narrative' | 'exploration' | 'tour';

declare global {
  interface Window {
    __planetModeController?: {
      toggle: () => void;
      setMode: (m: Mode) => void;
    };
  }
}

function readMode(): Mode {
  if (typeof window === 'undefined') return 'narrative';
  const v = window.__planetMode;
  return v === 'exploration' || v === 'tour' ? v : 'narrative';
}

const SEGMENTS: { mode: Mode; label: string; icon: 'compass' | 'orbit' | 'play' }[] = [
  { mode: 'narrative', label: 'NARRATIVE', icon: 'compass' },
  { mode: 'exploration', label: 'EXPLORE', icon: 'orbit' },
  { mode: 'tour', label: 'TOUR', icon: 'play' },
];

export function ModeToggle() {
  const [mode, setMode] = useState<Mode>(readMode);

  // Poll the global mode value — the bus is module-singleton and the
  // cost is a single ref read every 100ms, which is negligible.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    setMode(readMode());
    const id = window.setInterval(() => {
      const cur = readMode();
      setMode((prev) => (prev === cur ? prev : cur));
    }, 100);
    return () => window.clearInterval(id);
  }, []);

  const handleSelect = (to: Mode) => {
    const from = readMode();
    if (from === to) return;
    if (typeof window !== 'undefined' && window.__planetModeController) {
      window.__planetModeController.setMode(to);
    } else if (typeof window !== 'undefined') {
      window.__planetMode = to;
      try {
        window.localStorage.setItem('yhazrin:mode', to);
      } catch {
        /* ignore */
      }
    }
    setMode(to);
    track('mode_toggle', { from, to });
  };

  return (
    <div
      className={['mode-toggle', `is-${mode}`].join(' ')}
      role="radiogroup"
      aria-label="Planet viewing mode"
      data-mode={mode}
    >
      {SEGMENTS.map(({ mode: segMode, label, icon }) => {
        const isActive = segMode === mode;
        return (
          <button
            key={segMode}
            type="button"
            role="radio"
            aria-checked={isActive}
            aria-label={label}
            title={`${label}${segMode === 'tour' ? ' — press T' : segMode === 'exploration' ? ' — press E' : ' — press E'}`}
            className={['mode-toggle__segment', isActive ? 'is-active' : '']
              .filter(Boolean)
              .join(' ')}
            onClick={() => handleSelect(segMode)}
          >
            <span className="mode-toggle__icon" aria-hidden="true">
              {icon === 'compass' ? (
                <svg
                  className="mode-toggle__glyph"
                  width="12"
                  height="12"
                  viewBox="0 0 14 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="7"
                    cy="7"
                    r="5.5"
                    stroke="currentColor"
                    strokeWidth="1"
                    fill="none"
                  />
                  <path
                    d="M7 2.5 L8.4 7 L7 11.5 L5.6 7 Z"
                    fill="currentColor"
                    opacity="0.85"
                  />
                  <circle cx="7" cy="7" r="0.8" fill="currentColor" />
                </svg>
              ) : icon === 'orbit' ? (
                <svg
                  className="mode-toggle__glyph"
                  width="12"
                  height="12"
                  viewBox="0 0 14 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <ellipse
                    cx="7"
                    cy="7"
                    rx="6"
                    ry="3"
                    stroke="currentColor"
                    strokeWidth="1"
                    fill="none"
                  />
                  <circle cx="7" cy="7" r="1.6" fill="currentColor" opacity="0.85" />
                </svg>
              ) : (
                <svg
                  className="mode-toggle__glyph"
                  width="12"
                  height="12"
                  viewBox="0 0 14 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M4.5 3 L11 7 L4.5 11 Z"
                    stroke="currentColor"
                    strokeWidth="1"
                    fill="currentColor"
                    fillOpacity="0.85"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </span>
            <span className="mode-toggle__label">{label}</span>
          </button>
        );
      })}
      <span className="mode-toggle__pill-indicator" aria-hidden="true" />
    </div>
  );
}

export default ModeToggle;
